import { Badge, Card } from '@bienenkampf/ui';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { BuildQueue } from '../components/BuildQueue.js';
import { BuildingList } from '../components/BuildingList.js';
import { QueenCard } from '../components/QueenCard.js';
import { QuestCard } from '../components/QuestCard.js';
import { SupportCard } from '../components/SupportCard.js';
import { WinterCard } from '../components/WinterCard.js';
import { ResourceBar } from '../components/ResourceBar.js';
import { TrainQueue } from '../components/TrainQueue.js';
import { UnitList } from '../components/UnitList.js';
import { useNow } from '../hooks/useNow.js';
import { formatAbsRel, formatDateTime, formatNumber } from '../lib/time.js';
import { pickActive, useActiveHiveId } from '../lib/activeHive.js';
import { useTRPC } from '../lib/trpc.js';

export function HivePage() {
  const { worldId } = useParams({ from: '/w/$worldId' });
  const trpc = useTRPC();
  const now = useNow();
  const me = useQuery(trpc.auth.me.queryOptions());
  const mine = useQuery(trpc.hive.mine.queryOptions({ worldId: Number(worldId) }));
  const activeId = useActiveHiveId(Number(worldId));
  const hiveId = pickActive(mine.data?.hives, activeId)?.id;
  const hive = useQuery({
    ...trpc.hive.get.queryOptions({ hiveId: hiveId ?? 0 }),
    enabled: hiveId !== undefined,
    // Sicherheitsnetz, falls ein WebSocket-Event verloren geht.
    refetchInterval: 60_000,
  });

  if (mine.isPending || (hiveId !== undefined && hive.isPending))
    return <p className="text-stone-400">Lade Stock…</p>;
  if (!hiveId) return <p className="text-stone-400">Du hast in dieser Welt noch keinen Stock.</p>;
  if (hive.isError) return <p className="text-red-400">{hive.error.message}</p>;
  const v = hive.data!;
  const player = me.data?.players.find((p) => p.worldId === Number(worldId));
  const protectedUntil =
    player?.protectionUntil && player.protectionUntil > now ? player.protectionUntil : null;
  const popFull = v.population.current >= v.population.cap;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-semibold">{v.name}</h1>
        <span className="text-sm text-stone-400">
          [{v.q}|{v.r}]
        </span>
        <Badge tone={v.season.season === 'WINTER' ? 'sky' : 'amber'}>
          {v.season.season === 'WINTER' ? 'Winter' : 'Tracht'} · bis{' '}
          {formatDateTime(v.season.endsAt)}
        </Badge>
        {protectedUntil && (
          <Badge tone="green" title={formatAbsRel(protectedUntil, now)}>
            Imkerschutz {formatAbsRel(protectedUntil, now)}
          </Badge>
        )}
        <Badge
          tone={popFull ? 'red' : 'neutral'}
          title="Bienen (inkl. Ausbildung) / Bevölkerungslimit"
        >
          🐝 {formatNumber(v.population.current)} / {formatNumber(v.population.cap)}
        </Badge>
        {!v.queen.ruling && <Badge tone="red">weisellos</Badge>}
        <span className="ml-auto text-sm text-stone-400">{v.points} Punkte</span>
      </div>

      <ResourceBar view={v} now={now} />
      <QuestCard worldId={Number(worldId)} hiveId={v.id} />

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <QueenCard view={v} now={now} />
        <WinterCard view={v} now={now} />
        <SupportCard worldId={Number(worldId)} view={v} now={now} />
        <Card title="Waben">
          <BuildingList view={v} now={now} />
        </Card>
        <Card title={`Bau-Queue (${v.queue.length}/${v.queueSlots})`}>
          <BuildQueue view={v} now={now} />
        </Card>
        <Card title="Bienen">
          <UnitList view={v} now={now} />
        </Card>
        <Card title={`Ausbildung (${v.trainQueue.length}/${v.trainQueueSlots})`}>
          <TrainQueue view={v} now={now} />
        </Card>
      </div>
    </div>
  );
}
